import React from 'react';

// Enhanced bundle optimization utilities
export const preloadRoute = (routeImport: () => Promise<any>) => {
  // Preload route component on idle with better error handling
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      routeImport().catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to preload route:', error);
        }
      });
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      routeImport().catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to preload route:', error);
        }
      });
    }, 2000);
  }
};

// Performance monitoring with Web Vitals
export const measureLoadTime = (componentName: string) => {
  const startTime = performance.now();
  
  return () => {
    const endTime = performance.now();
    const loadTime = endTime - startTime;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`${componentName} loaded in ${loadTime.toFixed(2)}ms`);
      
      // Log slow components
      if (loadTime > 100) {
        console.warn(`⚠️ Slow component detected: ${componentName} took ${loadTime.toFixed(2)}ms`);
      }
    }
    
    // Report to performance API if available
    if ('performance' in window && 'measure' in performance) {
      try {
        performance.mark(`${componentName}-end`);
        performance.measure(`${componentName}-load`, `${componentName}-start`, `${componentName}-end`);
      } catch (error) {
        // Ignore performance API errors
      }
    }
  };
};

// Component wrapper for performance tracking
export const withPerformanceTracking = (
  WrappedComponent: React.ComponentType<any>,
  componentName?: string
) => {
  const displayName = componentName || WrappedComponent.displayName || WrappedComponent.name;
  
  const PerformanceTrackedComponent = (props: any) => {
    React.useEffect(() => {
      const cleanup = measureLoadTime(displayName);
      return cleanup;
    }, []);

    return React.createElement(WrappedComponent, props);
  };

  PerformanceTrackedComponent.displayName = `withPerformanceTracking(${displayName})`;
  return PerformanceTrackedComponent;
};

// Lazy load with preloading
export const createLazyComponent = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  preload: boolean = false
) => {
  const LazyComponent = React.lazy(importFn);
  
  if (preload) {
    // Preload on next tick
    setTimeout(() => preloadRoute(importFn), 0);
  }
  
  return LazyComponent;
};

// Resource hints for better loading
export const addResourceHints = (urls: string[], type: 'preload' | 'prefetch' = 'prefetch') => {
  urls.forEach(url => {
    const link = document.createElement('link');
    link.rel = type;
    link.href = url;
    
    if (type === 'preload') {
      // Determine resource type
      if (url.endsWith('.js')) {
        link.as = 'script';
      } else if (url.endsWith('.css')) {
        link.as = 'style';
      } else if (url.match(/\.(png|jpg|jpeg|gif|webp)$/)) {
        link.as = 'image';
      }
    }
    
    document.head.appendChild(link);
  });
};

// Memory usage monitoring
export const monitorMemoryUsage = () => {
  if ('memory' in performance) {
    const memInfo = (performance as any).memory;
    
    const usage = {
      used: Math.round(memInfo.usedJSHeapSize / 1048576), // MB
      total: Math.round(memInfo.totalJSHeapSize / 1048576), // MB
      limit: Math.round(memInfo.jsHeapSizeLimit / 1048576), // MB
      percentage: Math.round((memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit) * 100)
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('Memory usage:', usage);
      
      if (usage.percentage > 80) {
        console.warn('⚠️ High memory usage detected:', usage);
      }
    }
    
    return usage;
  }
  
  return null;
};

// Debounced function creator for performance
export const createDebouncedFunction = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T => {
  let timeoutId: NodeJS.Timeout;
  
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
};

// Check if device is low-end for performance optimizations
export const isLowEndDevice = (): boolean => {
  // Check for service worker support (proxy for modern browser)
  if (!('serviceWorker' in navigator)) return true;
  
  // Check device memory if available
  if ('deviceMemory' in navigator) {
    return (navigator as any).deviceMemory <= 2; // 2GB or less
  }
  
  // Check hardware concurrency (CPU cores)
  if ('hardwareConcurrency' in navigator) {
    return navigator.hardwareConcurrency <= 2; // 2 cores or less
  }
  
  // Fallback: assume not low-end
  return false;
};

// Adaptive loading based on network conditions
export const getNetworkQuality = (): 'slow' | 'fast' | 'unknown' => {
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    
    if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
      return 'slow';
    }
    
    if (connection.effectiveType === '3g' && connection.rtt > 300) {
      return 'slow';
    }
    
    return 'fast';
  }
  
  return 'unknown';
};